// The league channel (CHAT.md): one list of messages per league that talk,
// AI talk, and the room's system posts all land in. The sim seeds a little
// history, narrates finals into it as the clock passes them, and lets AI
// managers taunt per their dials. Everything is deterministic by seed and
// time so a reload replays the same channel.

import {
  botFace,
  pulseItemOf,
  readableColor,
  stickerOf,
  stickerSaid,
  type ChatCard,
  type ChatMessage,
  type MatchupSummary,
  type PulseItem,
  type StickerId,
} from '@fsp/domain';
import { botSticker, castMember, isStory, resultOccasion, say as botSays } from '@fsp/bots';
import type { SimState, SimTeam } from './league';
import { hashSeed, rng, talkDraws } from './rng';

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

let seq = 0;
export function systemMessage(
  state: SimState,
  at: number,
  body: string,
  card?: ChatCard,
): ChatMessage {
  return {
    id: `${state.leagueId}-sys-${at}-${seq++}`,
    kind: 'system',
    author: 'Room',
    authorColor: null,
    authorFace: null,
    body,
    at: iso(at),
    reactions: {},
    ...(card ? { card } : {}),
  };
}

/**
 * A bot's line's face (B-525): its character, dressed for the day, on its
 * team's colors, as a real league's bot line wears it; a person's line in the
 * tour has none, the tour keeping no account faces.
 */
export function botLineFace(
  team: Pick<SimTeam, 'ai' | 'colors'>,
  at: number,
): ChatMessage['authorFace'] {
  const face = team.ai === null ? null : botFace(team.ai.presetId, new Date(at));
  return face === null
    ? null
    : {
        face,
        colors: {
          primary: team.colors.primary.toLowerCase(),
          secondary: team.colors.secondary.toLowerCase(),
        },
      };
}

function say(state: SimState, teamIndex: number, at: number, body: string): ChatMessage | null {
  const team = state.teams[teamIndex];
  if (!team || !team.ownerName) return null;
  return {
    id: `${state.leagueId}-seed-${at}-${seq++}`,
    kind: team.ai ? 'ai' : 'user',
    author: team.ownerName,
    authorColor: readableColor(team.colors),
    authorFace: botLineFace(team, at),
    body,
    at: iso(at),
    reactions: {},
  };
}

/** A little history so a league never opens on an empty channel. */
export function seedChat(state: SimState): ChatMessage[] {
  const H = 3600 * 1000;
  const sun = state.sundayUtcMidnight;
  const out: (ChatMessage | null)[] = [];
  if (state.status === 'in-season') {
    const mine = state.teams.find((t) => t.isMine)!;
    const other = state.teams.find((t) => !t.isMine && t.ownerName);
    const p = (id: string) => state.players.get(id)?.name ?? 'somebody';
    out.push(
      say(state, 1, sun - 2 * 24 * H, `who is starting ${p(mine.roster[2] ?? '')} this week`),
      say(state, 0, sun - 2 * 24 * H + 5 * 60 * 1000, 'me. obviously'),
      say(
        state,
        1,
        sun + 14 * H,
        `${p(other?.roster[3] ?? '')} for your ${p(mine.roster[1] ?? '')}. fair deal and you know it`,
      ),
      say(state, 2, sun + 14 * H + 2 * 60 * 1000, 'it is not'),
      say(state, 1, sun + 14 * H + 4 * 60 * 1000, 'nobody asked you'),
    );
  } else {
    const commish = state.teams.findIndex((t) => t.ownerName === state.commissionerName);
    const other = state.teams.findIndex((t, i) => i !== commish && t.ownerName);
    out.push(
      say(state, other, sun - 3 * 24 * H, 'who still needs to join? still open seats'),
      say(
        state,
        commish,
        sun - 3 * 24 * H + 60 * 60 * 1000,
        state.draftAt === null
          ? 'league is up. sending the invite link now'
          : 'sent the link again. draft is coming up, be there',
      ),
    );
  }
  return out.filter((m): m is ChatMessage => m !== null);
}

/**
 * A sticker on a team as the practice league draws it (PLAN-2026-10-30-
 * stickers §3): its character for a bot's seat, its initials on a placard for
 * yours, the tour keeping no badge for your team.
 */
function stickerCard(team: SimTeam, sticker: StickerId): ChatCard | null {
  const known = stickerOf(sticker);
  if (known === undefined) return null;
  return {
    kind: 'sticker',
    sticker: known.id,
    words: known.words,
    team: {
      abbreviation: team.abbreviation,
      colors: {
        primary: team.colors.primary.toLowerCase(),
        secondary: team.colors.secondary.toLowerCase(),
      },
      badge:
        team.ai !== null && botFace(team.ai.presetId) !== null
          ? { kind: 'bot', id: team.ai.presetId }
          : null,
    },
  };
}

export function postChat(
  state: SimState,
  msg: {
    author: string;
    /** The words; a sticker's own when `sticker` is set. */
    body: string;
    color: string | null;
    at: number;
    kind?: ChatMessage['kind'];
    /** The line it answers (PLAN-2026-10-27-more-fun-in-chat C2), quoted over it. */
    replyTo?: string | null;
    /** A sticker, drawn with my team (PLAN-2026-10-30-stickers). */
    sticker?: StickerId;
  },
): ChatMessage {
  const answered =
    msg.replyTo === undefined || msg.replyTo === null
      ? undefined
      : state.chat.find((x) => x.id === msg.replyTo);
  const mine = state.teams.find((t) => t.isMine);
  const card =
    msg.sticker === undefined || mine === undefined ? null : stickerCard(mine, msg.sticker);
  const m: ChatMessage = {
    id: `${state.leagueId}-msg-${msg.at}-${state.chat.length}`,
    kind: msg.kind ?? 'user',
    author: msg.author,
    authorColor: msg.color,
    authorFace: null,
    body: card?.kind === 'sticker' ? stickerSaid(card.words) : msg.body,
    ...(card === null ? {} : { card }),
    at: iso(msg.at),
    reactions: {},
    ...(answered
      ? {
          replyTo: {
            id: answered.id,
            author: answered.kind === 'system' ? null : answered.author,
            body: answered.body,
          },
        }
      : {}),
  };
  state.chat.push(m);
  return m;
}

/** Toggle one person's reaction on a message. Returns false when the message is unknown. */
export function reactChat(state: SimState, messageId: string, emoji: string, who: string): boolean {
  const m = state.chat.find((x) => x.id === messageId);
  if (!m) return false;
  const list = m.reactions[emoji] ?? [];
  m.reactions[emoji] = list.includes(who) ? list.filter((w) => w !== who) : [...list, who];
  if (m.reactions[emoji].length === 0) delete m.reactions[emoji];
  return true;
}

const GAME_MS = 11700 * 1000;

/**
 * Post what the clock has passed: the period's finals once every game has
 * ended, then one AI taunt per AI seat with something to say. Idempotent.
 */
export function advanceChat(
  state: SimState,
  now: number,
  summarize: (matchupId: string, at: number) => MatchupSummary,
): boolean {
  if (state.status !== 'in-season' || state.proGames.length === 0) return false;
  const ends = state.proGames.map((g) => g.startAt + GAME_MS);
  const finalAt = Math.max(...ends);
  const key = `final:${state.currentPeriod}`;
  if (now < finalAt || state.chatFlags.has(key)) return false;
  state.chatFlags.add(key);

  // Finals from the same numbers the matchup screen shows.
  const matchups = state.schedule.filter((m) => m.period === state.currentPeriod);
  for (const m of matchups) {
    const s = summarize(m.id, finalAt);
    const winner = s.home.score >= s.away.score ? s.home : s.away;
    const loser = winner === s.home ? s.away : s.home;
    state.chat.push(
      systemMessage(
        state,
        finalAt + 60_000,
        `Final: ${winner.team.name} ${winner.score.toFixed(1)}, ${loser.team.name} ${loser.score.toFixed(1)}.`,
      ),
    );
  }
  // ONE BOT AT MOST, ABOUT A WEEK WORTH IT (DIRECTION A4), as a real league's
  // morning look says it (`bot-talk.ts`): a game decided by under five points
  // or by thirty or more, in the bot's own words; of those whose dial lets
  // them, the most eager. The draft room's lines once stood in here, with a
  // team name where a player's went (AUDIT-2026-09-19 F26).
  let speaker: {
    at: number;
    line: string;
    team: (typeof state.teams)[number];
    sticker: StickerId | null;
  } | null = null;
  for (const team of state.teams) {
    if (!team.ai) continue;
    const m = matchups.find((x) => x.home === team.id || x.away === team.id);
    if (!m) continue;
    const s = summarize(m.id, finalAt);
    const me = s.home.team.id === team.id ? s.home : s.away;
    const them = me === s.home ? s.away : s.home;
    const mine = Math.round(me.score * 10) / 10;
    const theirs = Math.round(them.score * 10) / 10;
    const occasion = resultOccasion(mine, theirs);
    const member = castMember(team.ai.presetId);
    if (!isStory(occasion) || member === null) continue;
    const draws = talkDraws(state.seed, `${key}:${team.id}`);
    if (speaker !== null && draws.random >= speaker.at) continue;
    const line = botSays(
      member,
      occasion,
      {
        opponent: them.team.name,
        owner: them.team.ownerName || them.team.name,
        score: mine.toFixed(1),
        theirs: theirs.toFixed(1),
        margin: Math.abs(mine - theirs).toFixed(1),
        week: state.currentPeriod,
        team: me.team.name,
      },
      draws,
    );
    // AND SOMETIMES A STICKER AFTER IT, as a real league's bot sends one.
    const stickerKey = `${key}:${team.id}:sticker`;
    const sticker = botSticker(occasion, member.dials.trashTalk, {
      sticker: rng(hashSeed(state.seed, stickerKey)).next(),
      which: rng(hashSeed(state.seed, `${stickerKey}:which`)).next(),
    });
    if (line !== null) speaker = { at: draws.random, line, team, sticker };
  }
  if (speaker !== null) {
    const said = {
      kind: 'ai' as const,
      author: speaker.team.ownerName,
      authorColor: readableColor(speaker.team.colors),
      authorFace: botLineFace(speaker.team, finalAt + 5 * 60_000),
      at: iso(finalAt + 5 * 60_000),
      reactions: {},
    };
    state.chat.push({
      ...said,
      id: `${state.leagueId}-ai-${key}-${speaker.team.id}`,
      body: speaker.line,
    });
    const card = speaker.sticker === null ? null : stickerCard(speaker.team, speaker.sticker);
    if (card?.kind === 'sticker')
      state.chat.push({
        ...said,
        id: `${state.leagueId}-ai-${key}-${speaker.team.id}-sticker`,
        body: stickerSaid(card.words),
        card,
      });
  }
  return true;
}

/**
 * The channel sorted by time; the hub, the drawer, and the draft room all read
 * this. `myReactions` from my team's owner name, which is how the simulator
 * records who reacted.
 */
export function chatView(state: SimState): ChatMessage[] {
  const me = state.teams.find((t) => t.isMine)?.ownerName ?? null;
  return [...state.chat]
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .map((m) => ({
      ...m,
      myReactions:
        me === null
          ? []
          : Object.entries(m.reactions)
              .filter(([, who]) => who.includes(me))
              .map(([emoji]) => emoji),
    }));
}

export { readableColor };

/** The last few messages as Home's pulse, as a real league's Home says them. */
export function pulseFromChat(state: SimState, count = 3): PulseItem[] {
  return chatView(state).slice(-count).map(pulseItemOf);
}
